import React from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Select, MenuItem, IconButton, CircularProgress, Tooltip,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import NoData from '../no-data';
import { AuditLogExplorerTypes } from '../../types/auditLogExplorer.types';
import { AUDIT_COLUMNS, ROWS_PER_PAGE_OPTIONS, actionLabel } from '../../builders/audit-log-explorer-builders';
import { MethodBadge, StatusBadge } from './audit-badges';
import { ALE } from './tokens';
import { formatTimestampParts, responseTimeTone } from './helpers';

const headCellSx = {
    bgcolor: ALE.headerStrip,
    fontWeight: 600,
    fontSize: '11px',
    letterSpacing: '.5px',
    textTransform: 'uppercase' as const,
    color: ALE.tMuted,
    padding: '11px 14px',
    borderBottom: `1px solid ${ALE.border}`,
    whiteSpace: 'nowrap' as const,
};
const bodyCellSx = {
    padding: '10px 14px',
    borderBottom: `1px solid ${ALE.border}`,
    color: ALE.tSecondary,
    whiteSpace: 'nowrap' as const,
    fontSize: '13px',
};

const respColor = (tone: ReturnType<typeof responseTimeTone>) =>
    tone === 'vslow' ? ALE.errorT : tone === 'slow' ? ALE.warnT : ALE.tSecondary;

export interface AuditResultsTableProps {
    result: AuditLogExplorerTypes.SearchResult | null;
    loading: boolean;
    page: number;
    limit: number;
    sortBy: AuditLogExplorerTypes.SortBy;
    sortOrder: AuditLogExplorerTypes.SortOrder;
    onSort: (sortBy: AuditLogExplorerTypes.SortBy) => void;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    onRowClick: (mongoId: string) => void;
    onLoadTotal: () => void;
    selectedMongoId?: string | null;
}

export const AuditResultsTable: React.FC<AuditResultsTableProps> = ({
    result, loading, page, limit, sortBy, sortOrder,
    onSort, onPageChange, onLimitChange, onRowClick, onLoadTotal, selectedMongoId,
}) => {
    const items = result?.items ?? [];
    const start = items.length ? (page - 1) * limit + 1 : 0;
    const end = (page - 1) * limit + items.length;

    return (
        <Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ borderCollapse: 'collapse' }}>
                    <TableHead>
                        <TableRow>
                            {AUDIT_COLUMNS.map((col) => {
                                const active = col.sortBy && col.sortBy === sortBy;
                                return (
                                    <TableCell
                                        key={col.field}
                                        align={col.align || 'left'}
                                        onClick={col.sortBy ? () => onSort(col.sortBy!) : undefined}
                                        sx={{ ...headCellSx, cursor: col.sortBy ? 'pointer' : 'default', userSelect: 'none' }}
                                    >
                                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px', justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
                                            {col.headerName}
                                            {active && (sortOrder === 'asc'
                                                ? <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                                                : <ArrowDownwardIcon sx={{ fontSize: 13 }} />)}
                                        </Box>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    {!loading && (
                        <TableBody>
                            {items.map((r) => {
                                const ts = formatTimestampParts(r.timestamp);
                                const tone = responseTimeTone(r.responseTimeMs);
                                const selected = selectedMongoId === r.mongoId;
                                const label = actionLabel(r.endpoint);
                                return (
                                    <TableRow
                                        key={r.mongoId}
                                        onClick={() => onRowClick(r.mongoId)}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: selected ? 'rgba(17,167,226,.07)' : 'transparent',
                                            '&:hover': { bgcolor: selected ? 'rgba(17,167,226,.07)' : ALE.rowHover },
                                        }}
                                    >
                                        <TableCell sx={bodyCellSx}>
                                            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: ALE.tPrimary, fontWeight: 500 }}>
                                                <Box component="span" sx={{ color: ALE.tFaint, fontWeight: 400, mr: '6px' }}>{ts.date}</Box>
                                                {ts.time}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}><MethodBadge method={r.method} /></TableCell>
                                        <TableCell sx={bodyCellSx}><StatusBadge code={r.statusCode} /></TableCell>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: ALE.tPrimary }}>
                                            <Tooltip title={label}>
                                                <Box component="span" sx={{ display: 'inline-block', maxWidth: '26ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                                                    {label}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Tooltip title={r.endpoint}>
                                                <Box component="span" sx={{ fontFamily: ALE.mono, fontSize: '12px', color: ALE.tPrimary, display: 'inline-block', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                                                    {r.endpoint}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ ...bodyCellSx, fontFamily: ALE.mono, fontSize: '12px', color: ALE.tMuted }}>{r.apiSource}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Box component="span" sx={{ fontFamily: ALE.mono, fontSize: '12px', color: ALE.tFaint }}>
                                                {r.subscriptionId ? `${r.subscriptionId.slice(0, 10)}…` : '—'}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                                                <Box component="span" sx={{ color: ALE.tPrimary, fontFamily: ALE.mono, fontSize: '11.5px' }}>
                                                    {r.userId ? `${r.userId.slice(0, 10)}…` : (r.userType || '—')}
                                                </Box>
                                                {r.userType && r.userId && <Box component="span" sx={{ fontSize: 11, color: ALE.tFaint }}>{r.userType}</Box>}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={{ ...bodyCellSx, fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: respColor(tone) }}>
                                            {r.responseTimeMs.toLocaleString()} ms
                                        </TableCell>
                                        <TableCell sx={{ ...bodyCellSx, textAlign: 'center', width: 40 }}>
                                            {(r.statusCode >= 400 || r.errorMessage) && (
                                                <Tooltip title={r.errorMessage || 'Error'}>
                                                    <ErrorOutlineIcon sx={{ fontSize: 16, color: ALE.errorT, verticalAlign: 'middle' }} />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>

            {loading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: '60px', color: ALE.tMuted, gap: 2 }}>
                    <CircularProgress size={34} sx={{ color: ALE.pMain }} />
                    <Typography sx={{ fontSize: 13 }}>Querying audit warehouse…</Typography>
                </Box>
            )}

            {/* The query effect auto-runs on mount, so results are always the
                outcome of a search — there is no distinct pre-search state. */}
            {!loading && items.length === 0 && (
                <NoData
                    buttonText={null}
                    onClick={() => {}}
                    title="No logs found"
                    description="No records match these filters. Try widening the time range or removing a filter."
                />
            )}

            {/* pagination — hasMore-based */}
            {!loading && items.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', p: '11px 16px', fontSize: '12.5px', color: ALE.tMuted }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>Rows per page</span>
                        <Select
                            size="small"
                            value={limit}
                            onChange={(e) => onLimitChange(Number(e.target.value))}
                            sx={{ fontSize: '12.5px', '& .MuiSelect-select': { padding: '4px 24px 4px 10px' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: ALE.g300 } }}
                        >
                            {ROWS_PER_PAGE_OPTIONS.map((n) => <MenuItem key={n} value={n} sx={{ fontSize: '12.5px' }}>{n}</MenuItem>)}
                        </Select>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <span>
                            {start.toLocaleString()}–{end.toLocaleString()}
                            {result?.total != null
                                ? ` of ${result.total.toLocaleString()}`
                                : (
                                    <Box component="span" onClick={onLoadTotal} sx={{ ml: '8px', color: ALE.pMain, cursor: 'pointer', fontWeight: 600 }}>
                                        Load total
                                    </Box>
                                )}
                        </span>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <IconButton size="small" disabled={page <= 1} onClick={() => onPageChange(page - 1)} sx={{ border: `1px solid ${ALE.g300}`, borderRadius: '6px' }}>
                                <KeyboardArrowLeftIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton size="small" disabled={!result?.hasMore} onClick={() => onPageChange(page + 1)} sx={{ border: `1px solid ${ALE.g300}`, borderRadius: '6px' }}>
                                <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
